export type Lang = 'en' | 'de';

const en = {
  // LoginPage
  loginEmailPlaceholder: 'Email',
  loginPasswordPlaceholder: 'Password',
  loginButton: 'Login',
  loggingIn: 'Logging in…',
  loginFailed: 'Invalid email or password. Please try again.',
  registrationFailed: 'Registration failed. Please try again.',
  forgotPassword: 'Forgot password?',
  noAccount: "Don't have an account?",
  registerHere: 'Register here!',
  resendVerification: 'Resend verification email',
  sendingVerification: 'Sending…',
  verificationResentMessage: "If your account needs verification, we've sent a fresh link to your inbox.",
  registerFirstName: 'First Name',
  registerLastName: 'Last Name',
  registerButton: 'Register',
  alreadyHaveAccount: 'Already have an account?',
  backToLogin: 'Back to login',

  // BookingPage header
  bookAnAppointment: 'Book an appointment',
  rescheduleAnAppointment: 'Reschedule your appointment',
  with: 'with',
  switchToLight: 'Switch to light mode',
  switchToDark: 'Switch to dark mode',
  switchToGerman: 'Auf Deutsch wechseln',
  switchToEnglish: 'Switch to English',
  errorSomethingWrong: 'Something went wrong.',
  errorDetails: 'Error Details',
  errorReloadPage: 'Reload Page',
  apiErrorTitle: 'Unable to load data',
  apiErrorMessage: "We're having trouble connecting to our servers. Please try again.",
  apiErrorRetry: 'Try Again',
  bookingLoadFailed: "Couldn't load this booking page",
  bookingLoadFailedDesc: 'The link may be invalid or the artist is no longer available.',
  bookingLoadingText: 'Loading…',
  serviceUnavailableHeading: 'Service Unavailable',
  serviceUnavailableMessage: 'The server could not be reached. Please check your connection or try again later.',
  serviceUnavailableRetry: 'Try Again',

  // Duration selector
  durationLabel: 'Duration:',
  oneHour: '1 hour',
  nHours: (n: number) => `${n} hours`,

  // SlotPicker
  selectDatePrompt: 'Please select a date to view available time slots',
  noAvailabilityFor: (date: string) => `No availability information for ${date}`,
  noSlotsFor: (duration: number, date: string) => `No available slots for ${duration} hour duration on ${date}`,
  availableOn: (date: string, duration: number) => `Available on ${date} (${duration}h appointment)`,
  pleaseSelectSlot: 'Please select a time slot',
  slotCountDay: 'day',
  slotCountDays: 'days',
  slotCountPrevLabel: 'Days with free slots in previous month',
  slotCountNextLabel: 'Days with free slots in next month',
  navPrevMonth: 'Previous month',
  navNextMonth: 'Next month',
  selectStartTime: 'Select appointment start time:',
  appointmentTime: (display: string, duration: number) =>
    `Appointment: ${display} (${duration} hour${duration !== 1 ? 's' : ''})`,
  selectSlot: 'Select',
  selectedSlot: 'Selected',
  toTime: (start: string, end: string) => `${start} to ${end}`,

  // Weekdays
  weekdays: [
    { abbr: 'MO', full: 'Monday' },
    { abbr: 'TU', full: 'Tuesday' },
    { abbr: 'WE', full: 'Wednesday' },
    { abbr: 'TH', full: 'Thursday' },
    { abbr: 'FR', full: 'Friday' },
    { abbr: 'SA', full: 'Saturday' },
    { abbr: 'SU', full: 'Sunday' },
  ],

  // CustomerForm
  bookYourAppointment: 'Book Your Appointment',
  errorLabel: 'Error:',
  yourName: 'Your Name:',
  enterYourName: 'Enter your full name',
  nameTooShort: 'Name must be at least 2 characters',
  yourEmail: 'Your Email:',
  enterYourEmail: 'Enter your email address',
  invalidEmail: 'Please enter a valid email address',
  descriptionOptional: 'Description (optional):',
  descriptionPlaceholder: 'Brief description of what we will do...',
  characters: 'characters',
  bookAppointment: 'Book Appointment',
  bookingInProgress: 'Booking...',

  // ThankYouPage
  almostThere: 'Almost there!',
  sentConfirmationTo: "We've sent a confirmation link to",
  clickLinkInstructions:
    'Click the link in that email to confirm your request. Unconfirmed slots are released automatically after a short while.',
  appointmentSummary: 'Appointment Summary',
  dateAndTime: 'Date & Time:',
  duration: 'Duration:',
  durationValue: (n: number) => `${n} hour(s)`,
  descriptionLabel: 'Description:',
  closeTab: 'You can safely close this tab.',

  // ConfirmPage
  confirmingAppointment: 'Confirming your appointment…',
  oneMoment: "One moment, this won't take long.",
  appointmentConfirmed: 'Appointment confirmed',
  thankYouConfirm:
    'Thanks for confirming. Your request has been sent to the artist for review — you’ll receive another email once they accept or decline.',
  somethingWentWrong: 'Something went wrong',

  // Forgot password (inline in LoginPage)
  forgotPasswordTitle: 'Reset your password',
  forgotPasswordSubtitle: "Enter the email you registered with and we'll send a reset link.",
  sendResetLink: 'Send reset link',
  checkYourInbox: 'Check your inbox',
  resetLinkSentMessage: "If you have an account, you'll receive an email with a link to reset your password. The link is valid for one hour.",

  // PasswordResetPage
  chooseNewPassword: 'Choose a new password',
  newPasswordPlaceholder: 'New password',
  confirmNewPasswordPlaceholder: 'Confirm new password',
  passwordTooShort: 'Password must be at least 8 characters.',
  passwordsNoMatch: "Passwords don't match.",
  updatePasswordButton: 'Update password',
  updatingPassword: 'Updating…',
  passwordUpdatedTitle: 'Password updated',
  passwordUpdatedMessage: 'You can now log in with your new password.',

  // PasswordChangeConfirmPage
  activatingPassword: 'Activating your new password…',
  passwordActivatedTitle: 'Password activated',

  // EmailChangeConfirmPage
  activatingEmail: 'Activating your new email…',
  emailUpdatedTitle: 'Email updated',
  emailUpdatedMessage: (email: string) => `Your account email is now ${email}. Use it the next time you log in.`,

  // VerifyEmailPage
  verifyingEmail: 'Verifying your email…',
  emailVerifiedTitle: 'Email verified',
  emailVerifiedMessage: (email: string) => `Thanks for confirming ${email}. Your account is active — you can log in now.`,
  verificationFailed: 'Verification failed',

  // GoogleOAuthCallbackPage
  connectingGoogleCalendar: 'Connecting your Google Calendar…',
  googleCalendarConnected: 'Google Calendar connected',
  googleCalendarSyncMessage: (email: string) => `Your appointments will now sync to ${email}.`,
  googleCalendarSyncMessageNoEmail: 'Your appointments will now sync to your Google Calendar.',
  googlePermissionsIncomplete: 'Permissions incomplete',
  googlePermissionsIncompleteMessage: "Google's consent screen lets you tick each permission individually, and one or more were left unchecked. Syncing won't work until every permission is granted.",
  googlePermissionsRetryMessage: 'Please go back to your dashboard, disconnect Google Calendar in Settings, and reconnect — leaving all checkboxes ticked.',
  googleCalendarError: "Couldn't connect Google Calendar",
  backToDashboard: 'Back to dashboard',

  // Legal pages
  imprintTitle: 'Imprint',
  privacyTitle: 'Privacy Policy',
  termsTitle: 'Terms of Service',
  backToHome: 'Back to home',
  back: 'Back',

  // BookingsOverview
  appointmentsHeading: 'Appointments',
  exportIcsButton: 'Export .ics',
  calendarFeedHeading: 'Calendar subscription',
  calendarFeedDescription: 'Subscribe to this URL in Apple Calendar, Google Calendar, or Outlook to get a live feed of your appointments.',
  calendarFeedCopied: 'Copied!',
  calendarFeedCopy: 'Copy URL',
  calendarFeedRegenerate: 'Regenerate',
  calendarFeedRegenerateWarning: 'Regenerating the URL will invalidate the old one. Any existing subscriptions will stop working.',
  includeDeclined: 'Include declined appointments',
  filterByMonth: 'Filter by month:',
  allMonths: 'All months',
  noAppointmentsYet: 'No appointments yet.',
  noAppointmentsMonth: 'No appointments found for this month.',
  colDate: 'Date',
  colCustomer: 'Customer',
  colLocation: 'Location',
  colEmail: 'E-mail',
  colTime: 'Time',
  colDescription: 'Description',
  noLocation: '— No location —',
  saveButton: 'Save',
  savingButton: 'Saving…',
  discardButton: 'Discard',
  acceptButton: 'Accept',
  declineButton: 'Decline',
  editButton: 'Edit',
  deleteButton: 'Delete',
  cancelButton: 'Cancel',
  cancelAppointmentConfirm: 'Cancel this appointment? The customer will be notified by email.',
  paginationPrev: '← Prev',
  paginationNext: 'Next →',
  paginationStatus: (page: number, totalPages: number, total: number) =>
    `Page ${page} of ${totalPages} · ${total} total`,

  // AvailabilityManager
  timeManagementHeading: 'Time Management',
  tabWorkingHours: 'Working Hours',
  tabVacations: 'Off-Days & Vacations',
  manageWeeklyAvailability: 'Manage Weekly Availability',
  toLabel: 'to',
  vacationManagementHeading: 'Vacation Management',
  addNewVacation: 'Add new vacation',
  failedLoadVacations: 'Failed to load vacations',
  failedLoadWorkingHours: 'Failed to load working hours.',
  failedSaveWorkingHours: 'Failed to save working hours. Please try again.',
  failedResetWorkingHours: 'Failed to reset working hours. Please try again.',
  workingHoursSaved: 'Working hours saved.',
  workingHoursReset: (day: string) => `${day} working hours reset.`,
  vacationFormTitleEdit: 'Edit Vacation',
  vacationFormTitleAdd: 'Add New Vacation',
  vacationTitleLabel: 'Title:',
  vacationStartDateLabel: 'Start Date:',
  vacationEndDateLabel: 'End Date:',
  updateVacationButton: 'Update Vacation',
  createVacationButton: 'Create Vacation',
  loadingVacations: 'Loading vacations...',
  noVacationsScheduled: 'No vacations scheduled.',
  dismissToast: 'Dismiss',
  validateBothHours: 'Please enter both start and end hours.',
  validateStartHour: 'Start hour must be between 0 and 23.',
  validateEndHour: 'End hour must be between 0 and 23.',
  validateEndAfterStart: 'End hour must be after start hour.',
  failedSaveVacation: 'Failed to save vacation',
  failedDeleteVacation: 'Failed to delete vacation',
  deleteVacationConfirm: 'Are you sure you want to delete this vacation?',

  // VacationManager
  vacationStatusActive: 'Active',
  vacationStatusUpcoming: 'Upcoming',
  vacationStatusPast: 'Past',
  addVacationButton: 'Add Vacation',
  vacationTitlePlaceholder: 'e.g. Summer vacation (optional)',

  // Recurring closures
  recurringClosuresHeading: 'Recurring days off',
  recurringClosuresSubheading: 'Block a weekday every N weeks — e.g. every 3rd Thursday.',
  recurringClosureWeekdayLabel: 'Weekday',
  recurringClosureIntervalLabel: 'Every',
  recurringClosureIntervalUnit: 'weeks',
  recurringClosureReferenceDateLabel: 'First occurrence',
  recurringClosureReferenceDateHint: 'Pick any date that falls on the chosen weekday and is an occurrence of this pattern.',
  recurringClosureAddButton: 'Add',
  recurringClosureError: 'Failed to update recurring day off',
  recurringClosureWrongWeekday: 'The selected date must be a',
  recurringClosureDisplay: (interval: number, weekdayName: string) =>
    `Every ${interval} week${interval > 1 ? 's' : ''} on ${weekdayName}`,
  recurringClosureNext: 'Next:',

  // LocationManager
  locationsHeading: 'Locations',
  addLocationButton: 'Add location',
  locationDefaultHint: 'Your default location is used for share links unless you pick a different one.',
  locationNameLabel: 'Name',
  locationAddressLabel: 'Address',
  locationUseAsDefault: 'Use as default',
  updateButton: 'Update',
  createButton: 'Create',
  loadingLocations: 'Loading locations…',
  noLocationsYet: 'No locations yet. Add one so customers know where to go.',
  defaultBadge: 'Default',
  makeDefaultButton: 'Make default',
  deleteLocationConfirm: (name: string) => `Delete location "${name}"?`,
  failedLoadLocations: 'Failed to load locations',
  failedSaveLocation: 'Failed to save location',
  failedSetDefaultLocation: 'Failed to set default location',
  failedDeleteLocation: 'Failed to delete location',

  // ProfileManager
  loadingProfile: 'Loading profile...',
  errorLabel2: 'Error:',
  bookingHorizonTitle: 'Booking horizon',
  bookingHorizonHint: 'How many days ahead customers can book. 7–365 days.',
  bookingHorizonSuccess: 'Booking horizon updated.',
  bookingHorizonError: 'Failed to update booking horizon',
  bookingHorizonValidate: 'Must be between 7 and 365 days.',
  breakTimeTitle: 'Break between appointments',
  breakTimeHint: 'Minutes reserved after each appointment before the next one can start. 0 = no gap.',
  breakTimeSuccess: 'Break time updated.',
  breakTimeError: 'Failed to update break time',
  breakTimeValidate: 'Must be between 0 and 120 minutes.',

  // MfaSetupPage
  secureYourAccount: 'Secure your account',
  mfaSetupIntro: 'Two-factor authentication adds a strong second layer of protection on top of your password.',
  mfaSetupIntroStrong: 'We strongly recommend turning it on',
  mfaSetupIntroReason: '— without it, anyone who learns your password can access your account.',
  mfaEmailLabel: 'Email code',
  mfaEmailDesc: 'Receive a 6-digit code by email each time you log in.',
  mfaAppLabel: 'Authenticator app',
  mfaAppDesc: 'Use Google Authenticator, Authy, or any TOTP app.',
  mfaSkipForNow: 'Skip for now',
  mfaSkipHint: 'You can enable two-factor authentication later from Settings.',
  mfaTotpInstructions: 'Open your authenticator app and add a new account. Enter the key below manually or scan it if your app supports URL import:',
  mfaTotpHint: 'Then enter the 6-digit code from your app to confirm:',
  mfaCodePlaceholder: '6-digit code',
  mfaVerifying: 'Verifying…',
  mfaConfirmEnable: 'Confirm & enable',
  mfaBack: 'Back',
  mfaChallengeHeading: 'Two-Factor Authentication',
  mfaChallengeTabTotp: 'Authenticator app',
  mfaChallengeTabEmail: 'Email code',
  mfaChallengeHintEmail: 'A verification code was sent to your email address.',
  mfaChallengeHintTotp: 'Enter the code from your authenticator app.',
  mfaCodePlaceholderChallenge: '6-digit code',
  mfaVerify: 'Verify',
  mfaResendCode: 'Resend code',
  mfaBackToLogin: 'Back to login',
  mfaCouldNotSkip: 'Could not skip — try again.',
  mfaFailedEnableEmail: 'Failed to enable email MFA.',
  mfaFailedStartSetup: 'Failed to start setup.',
  mfaInvalidCode: 'Invalid code — try again.',

  // Dashboard / ProfileOverlay
  editProfileHeading: 'Edit Profile',
  closeProfileEditor: 'Close profile editor',

  // DashboardTopbar
  navAppointments: 'Appointments',
  navAvailability: 'Availability',
  welcomeGreeting: (firstName: string) => `Welcome, ${firstName}`,
  openProfileMenu: 'Open profile menu',
  menuSettings: 'Settings',
  menuAbout: 'About & Help',
  menuSignOut: 'Sign out',

  // AboutOverlay
  aboutHeading: 'About & Help',
  closeAbout: 'Close',
  aboutWorkingHoursTitle: 'Setting up working hours',
  aboutWorkingHoursBody: 'Go to Settings → Working Hours to define on which days and times you are available. Customers will only see slots that fall within your working hours and that are not blocked by a vacation or recurring closure.',
  aboutBookingLinkTitle: 'Your booking link',
  aboutBookingLinkBody: 'Share your personal booking link with customers so they can book a slot directly. You can find and copy the link on the dashboard. Optionally set a custom URL slug in Settings → Profile so the link reflects your name.',
  aboutCalendarTitle: 'Calendar subscription',
  aboutCalendarBody: 'In Settings → Calendar you can copy a private feed URL and paste it into Google Calendar, Apple Calendar, or any other calendar app. Your confirmed appointments will appear there automatically and stay in sync.',
  aboutAppointmentsTitle: 'Managing appointments',
  aboutAppointmentsBody: 'New booking requests arrive in the Appointments view. Accept or decline them with one click. You can also cancel an existing appointment, or accept a reschedule request from a customer. The customer receives an email for every status change.',

  // SchedulingLinkCard
  schedulingLinkHeading: 'Send scheduling link',
  chooseDurationLabel: 'Choose appointment duration:',
  chooseAddressLabel: 'Choose appointment address:',
  customerChooses: 'Customer chooses',
  noLocationsHint: 'No locations yet — add one in Settings so customers know where to go.',
  copyButton: 'Copy',
  copiedButton: 'Copied!',
  publicIdHint: (id: string) => `Public ID: ${id}`,
  customPublicIdSuffix: ' (custom)',

  // GoogleCalendarSection
  googleCalendarHeading: 'Google Calendar',
  googleCalendarDesc: 'Sync confirmed and accepted appointments to your Google Calendar. Bookings appear as events automatically and update when rescheduled.',
  googleCalendarLoading: 'Loading…',
  googleCalendarStatusConnected: 'Connected',
  googleDisconnecting: 'Disconnecting…',
  googleDisconnect: 'Disconnect',
  googleMissingScopes: 'Some required permissions weren\'t granted.',
  googleMissingScopesDetail: "Google's consent screen lets you tick each permission individually, and one or more were left unchecked, so syncing won't work fully. Please Disconnect and reconnect, making sure to leave all permission checkboxes ticked.",
  googleSyncTo: 'Sync to',
  googleLoadingCalendars: 'Loading calendars…',
  googleNoWritableCalendars: 'No writable calendars found on this Google account.',
  googleCalendarSwitching: 'Switching calendars… existing events will be moved over in the background.',
  googleConnectButton: 'Connect Google Calendar',
  googleRedirecting: 'Redirecting…',
  googleFailedLoadStatus: 'Failed to load Google Calendar status',
  googleCouldNotLoadCalendars: 'Could not load your Google calendars',
  googleCouldNotStartSignIn: 'Could not start Google sign-in',
  googleCouldNotDisconnect: 'Could not disconnect Google Calendar',
  googleCouldNotChangeCalendar: 'Could not change the calendar',

  // DurationSettings
  durationSettingsHeading: 'Appointment durations offered',
  durationSettingsHint: 'Customers will pick from this list. Locked share links must use one of these values.',
  durationRemoveAriaLabel: (d: number) => `Remove ${d} hour option`,
  durationDefaultLabel: 'Default duration:',
  durationNoDefault: 'No default',
  durationAddPlaceholder: 'e.g. 1.5',
  durationAddButton: 'Add',
  durationSavingButton: 'Saving…',
  durationUpdated: 'Duration options updated',
  durationDefaultUpdated: 'Default duration updated',
  durationFailedUpdate: 'Failed to update duration options',
  durationFailedUpdateDefault: 'Failed to update default duration',
  durationErrorRange: 'Duration must be between 0.5 and 8.0 hours',
  durationErrorDuplicate: 'That duration is already in the list',
  durationErrorMinOne: 'At least one duration option is required',

  // ProfileForm
  firstNameLabel: 'First Name',
  lastNameLabel: 'Last Name',
  emailAddressLabel: 'Email Address',
  updatingButton: 'Updating...',
  updateProfileButton: 'Update Profile',
  firstNameRequired: 'First name is required',
  firstNameTooShort: 'First name must be at least 2 characters',
  lastNameRequired: 'Last name is required',
  lastNameTooShort: 'Last name must be at least 2 characters',
  emailRequired: 'Email is required',
  pendingEmailHint: (email: string) =>
    `Pending change to ${email} — click the link we sent to that address to activate it.`,
  profileUpdateFailed: 'Failed to update profile. Please try again.',

  // MfaSettings
  twoFactorHeading: 'Two-Factor Authentication',
  mfaEmailMethodLabel: 'Email code',
  mfaEmailMethodDesc: 'Receive a 6-digit code by email on each login.',
  mfaAppMethodLabel: 'Authenticator app',
  mfaAppMethodDesc: 'Use Google Authenticator, Authy, or any TOTP app.',
  mfaDisableButton: 'Disable',
  mfaEnableButton: 'Enable',
  mfaDisabling: 'Disabling…',
  mfaConfirmButton: 'Confirm',
  mfaCurrentPasswordPlaceholder: 'Current password',
  mfaTotpInstructionsSettings: 'Enter the key below in your authenticator app or scan if it supports URL import:',
  mfaTotpHintSettings: 'Then enter the 6-digit code to confirm:',
  mfaConfirmEnableButton: 'Confirm & enable',
  mfaVerifyingButton: 'Verifying…',
  mfaEmailEnabled: 'Email MFA enabled. You will receive a code by email on every login.',
  mfaEmailDisabled: 'Email MFA has been disabled.',
  mfaTotpEnabled: 'Authenticator app MFA enabled.',
  mfaTotpDisabled: 'Authenticator app MFA has been disabled.',
  mfaFailedEnableEmailSettings: 'Failed to enable email MFA.',
  mfaFailedDisableEmail: 'Failed to disable email MFA.',
  mfaFailedStartTotp: 'Failed to start TOTP setup.',
  mfaInvalidCodeSettings: 'Invalid code — try again.',
  mfaFailedDisableTotp: 'Failed to disable authenticator MFA.',

  // CustomSlugForm
  customSlugHeading: 'Custom share link',
  customSlugHint: '3–32 characters, lowercase letters/digits/hyphens. Leave empty to keep the random ID.',
  customSlugPlaceholder: 'e.g. jane-tattoo',
  customSlugSaving: 'Saving…',
  customSlugClear: 'Clear',
  customSlugSave: 'Save',
  customSlugUpdated: 'Custom share link updated.',
  customSlugCleared: 'Custom share link cleared. Your randomized ID is in use again.',
  customSlugFailed: 'Failed to update custom-id',

  // NumericFieldForm
  numericSaving: 'Saving…',

  // PasswordChangeForm
  changePasswordHeading: 'Change password',
  changePasswordHint: "We'll email a confirmation link to your registered address. Your existing password keeps working until you click the link.",
  currentPasswordLabel: 'Current password',
  newPasswordLabel: 'New password',
  confirmNewPasswordLabel: 'Confirm new password',
  sendingButton: 'Sending…',
  changePasswordButton: 'Change password',
  newPasswordTooShort: 'New password must be at least 8 characters.',
  passwordsDoNotMatch: "New password and confirmation don't match.",
  passwordChangeFailed: 'Failed to request password change',
  passwordChangeConfirmationSent: 'Confirmation email sent. Click the link to activate your new password.',

  // LogoUploadForm
  profilePictureHeading: 'Profile picture',
  uploadingButton: 'Uploading…',
  changePictureButton: 'Change picture',
  uploadPictureButton: 'Upload picture',
  removePictureButton: 'Remove',
  logoHint: 'JPEG, PNG, WebP or GIF · max 5 MB',
  logoAlt: 'Profile logo',
  logoUpdated: 'Logo updated.',
  logoUploadFailed: 'Upload failed. Please try again.',
  logoRemoveFailed: 'Failed to remove logo.',

  // Bio on booking page
  bioSectionTitle: 'About',

  // BioForm
  bioHeading: 'About you',
  bioLabel: 'Bio',
  bioPlaceholder: 'A short introduction visible to customers on your booking page (max 500 characters)',
  bioSaveButton: 'Save bio',
  bioSaving: 'Saving…',
  bioSaved: 'Bio saved.',
  bioSaveFailed: 'Failed to save bio. Please try again.',

  // CancelPage
  loadingAppointment: 'Loading appointment…',
  cancelQuestion: 'Cancel this appointment?',
  cancelling: 'Cancelling…',
  yesCancel: 'Yes, cancel',
  rescheduleInstead: 'Reschedule instead',
  noCancel: "If you don't want to cancel, just close this tab.",
  rescheduleRequestBanner: 'You are requesting to reschedule your appointment.',
  rescheduleSubmitButton: 'Request Reschedule',
  rescheduleSubmitting: 'Sending request…',
  rescheduleRequestSent: 'Reschedule request sent',
  rescheduleRequestSentMessage: 'Your request has been sent to the artist. You will receive an email once they accept or decline.',
  rescheduleRequestLabel: 'Reschedule request',
  rescheduleCurrentTime: 'Current time',
  rescheduleProposedTime: 'Proposed new time',
  acceptReschedule: 'Accept reschedule',
  declineReschedule: 'Decline reschedule',
  appointmentCancelled: 'Appointment cancelled',
  appointmentCancelledMessage: 'Your appointment has been cancelled. The artist has been notified.',
  bookNewAppointment: 'Book a new appointment',
  alreadyCancelled: 'Already cancelled',
  alreadyCancelledMessage: 'This appointment has already been cancelled.',
  nothingToCancel: 'Nothing to cancel',
  notCancellableMessage: 'This appointment is no longer active.',
};

const de: typeof en = {
  // LoginPage
  loginEmailPlaceholder: 'E-Mail',
  loginPasswordPlaceholder: 'Passwort',
  loginButton: 'Anmelden',
  loggingIn: 'Anmeldung läuft…',
  loginFailed: 'Ungültige E-Mail oder Passwort. Bitte erneut versuchen.',
  registrationFailed: 'Registrierung fehlgeschlagen. Bitte erneut versuchen.',
  forgotPassword: 'Passwort vergessen?',
  noAccount: 'Noch kein Konto?',
  registerHere: 'Hier registrieren!',
  resendVerification: 'Bestätigungsmail erneut senden',
  sendingVerification: 'Wird gesendet…',
  verificationResentMessage: 'Falls dein Konto noch nicht bestätigt wurde, haben wir dir einen neuen Link geschickt.',
  registerFirstName: 'Vorname',
  registerLastName: 'Nachname',
  registerButton: 'Registrieren',
  alreadyHaveAccount: 'Bereits ein Konto?',
  backToLogin: 'Zurück zur Anmeldung',

  bookAnAppointment: 'Termin buchen',
  rescheduleAnAppointment: 'Termin verschieben',
  with: 'bei',
  switchToLight: 'Zum hellen Modus wechseln',
  switchToDark: 'Zum dunklen Modus wechseln',
  switchToGerman: 'Auf Deutsch wechseln',
  switchToEnglish: 'Switch to English',
  errorSomethingWrong: 'Es ist ein Fehler aufgetreten.',
  errorDetails: 'Fehlerdetails',
  errorReloadPage: 'Seite neu laden',
  apiErrorTitle: 'Daten konnten nicht geladen werden',
  apiErrorMessage: 'Es gibt Verbindungsprobleme mit dem Server. Bitte versuche es erneut.',
  apiErrorRetry: 'Erneut versuchen',
  bookingLoadFailed: 'Diese Buchungsseite konnte nicht geladen werden',
  bookingLoadFailedDesc: 'Der Link ist möglicherweise ungültig oder der Anbieter ist nicht mehr verfügbar.',
  bookingLoadingText: 'Wird geladen…',
  serviceUnavailableHeading: 'Dienst nicht verfügbar',
  serviceUnavailableMessage: 'Der Server konnte nicht erreicht werden. Bitte überprüfe deine Verbindung oder versuche es später erneut.',
  serviceUnavailableRetry: 'Erneut versuchen',

  durationLabel: 'Dauer:',
  oneHour: '1 Stunde',
  nHours: (n: number) => `${n} Stunden`,

  selectDatePrompt: 'Bitte wähle ein Datum, um verfügbare Zeitslots zu sehen',
  noAvailabilityFor: (date: string) => `Keine Verfügbarkeitsinfo für ${date}`,
  noSlotsFor: (duration: number, date: string) => `Keine freien Slots für ${duration}h am ${date}`,
  availableOn: (date: string, duration: number) => `Verfügbar am ${date} (${duration}h-Termin)`,
  pleaseSelectSlot: 'Bitte wähle einen Zeitslot',
  slotCountDay: 'Tag',
  slotCountDays: 'Tage',
  slotCountPrevLabel: 'Tage mit freien Slots im Vormonat',
  slotCountNextLabel: 'Tage mit freien Slots im Folgemonat',
  navPrevMonth: 'Vorheriger Monat',
  navNextMonth: 'Nächster Monat',
  selectStartTime: 'Startzeit wählen:',
  appointmentTime: (display: string, duration: number) =>
    `Termin: ${display} (${duration} Stunde${duration !== 1 ? 'n' : ''})`,
  selectSlot: 'Wählen',
  selectedSlot: 'Gewählt',
  toTime: (start: string, end: string) => `${start} bis ${end}`,

  weekdays: [
    { abbr: 'MO', full: 'Montag' },
    { abbr: 'DI', full: 'Dienstag' },
    { abbr: 'MI', full: 'Mittwoch' },
    { abbr: 'DO', full: 'Donnerstag' },
    { abbr: 'FR', full: 'Freitag' },
    { abbr: 'SA', full: 'Samstag' },
    { abbr: 'SO', full: 'Sonntag' },
  ],

  bookYourAppointment: 'Termin buchen',
  errorLabel: 'Fehler:',
  yourName: 'Dein Name:',
  enterYourName: 'Vollständigen Namen eingeben',
  nameTooShort: 'Name muss mindestens 2 Zeichen lang sein',
  yourEmail: 'Deine E-Mail:',
  enterYourEmail: 'E-Mail-Adresse eingeben',
  invalidEmail: 'Bitte gib eine gültige E-Mail-Adresse ein',
  descriptionOptional: 'Beschreibung (optional):',
  descriptionPlaceholder: 'Kurze Beschreibung, was wir machen werden...',
  characters: 'Zeichen',
  bookAppointment: 'Termin buchen',
  bookingInProgress: 'Wird gebucht...',

  almostThere: 'Fast geschafft!',
  sentConfirmationTo: 'Wir haben einen Bestätigungslink gesendet an',
  clickLinkInstructions:
    'Klicke auf den Link in dieser E-Mail, um deine Anfrage zu bestätigen. Unbestätigte Slots werden automatisch nach kurzer Zeit freigegeben.',
  appointmentSummary: 'Terminzusammenfassung',
  dateAndTime: 'Datum & Uhrzeit:',
  duration: 'Dauer:',
  durationValue: (n: number) => `${n} Stunde${n !== 1 ? 'n' : ''}`,
  descriptionLabel: 'Beschreibung:',
  closeTab: 'Du kannst diesen Tab jetzt schließen.',

  confirmingAppointment: 'Termin wird bestätigt…',
  oneMoment: 'Einen Moment, das dauert nicht lange.',
  appointmentConfirmed: 'Termin bestätigt',
  thankYouConfirm:
    'Vielen Dank für die Bestätigung. Deine Anfrage wurde an den Künstler weitergeleitet – du erhältst eine E-Mail, sobald er annimmt oder ablehnt.',
  somethingWentWrong: 'Etwas ist schiefgelaufen',

  loadingAppointment: 'Termin wird geladen…',
  cancelQuestion: 'Diesen Termin absagen?',
  cancelling: 'Wird abgesagt…',
  yesCancel: 'Ja, absagen',
  rescheduleInstead: 'Stattdessen verschieben',
  noCancel: 'Wenn du nicht absagen möchtest, schließe einfach diesen Tab.',
  rescheduleRequestBanner: 'Du beantragst eine Verschiebung deines Termins.',
  rescheduleSubmitButton: 'Verschiebung beantragen',
  rescheduleSubmitting: 'Anfrage wird gesendet…',
  rescheduleRequestSent: 'Verschiebungsanfrage gesendet',
  rescheduleRequestSentMessage: 'Deine Anfrage wurde an den Künstler gesendet. Du erhältst eine E-Mail, sobald er annimmt oder ablehnt.',
  rescheduleRequestLabel: 'Verschiebungsanfrage',
  rescheduleCurrentTime: 'Aktueller Termin',
  rescheduleProposedTime: 'Gewünschter neuer Termin',
  acceptReschedule: 'Verschiebung annehmen',
  declineReschedule: 'Verschiebung ablehnen',
  appointmentCancelled: 'Termin abgesagt',
  appointmentCancelledMessage: 'Dein Termin wurde abgesagt. Der Künstler wurde benachrichtigt.',
  bookNewAppointment: 'Neuen Termin buchen',
  alreadyCancelled: 'Bereits abgesagt',
  alreadyCancelledMessage: 'Dieser Termin wurde bereits abgesagt.',
  nothingToCancel: 'Nichts abzusagen',
  notCancellableMessage: 'Dieser Termin ist nicht mehr aktiv.',

  // Forgot password (inline in LoginPage)
  forgotPasswordTitle: 'Passwort zurücksetzen',
  forgotPasswordSubtitle: 'Gib deine registrierte E-Mail-Adresse ein und wir senden dir einen Link.',
  sendResetLink: 'Link senden',
  checkYourInbox: 'Posteingang prüfen',
  resetLinkSentMessage: 'Falls du ein Konto hast, erhältst du eine E-Mail mit einem Link zum Zurücksetzen. Der Link ist eine Stunde lang gültig.',

  // PasswordResetPage
  chooseNewPassword: 'Neues Passwort wählen',
  newPasswordPlaceholder: 'Neues Passwort',
  confirmNewPasswordPlaceholder: 'Neues Passwort bestätigen',
  passwordTooShort: 'Das Passwort muss mindestens 8 Zeichen lang sein.',
  passwordsNoMatch: 'Passwörter stimmen nicht überein.',
  updatePasswordButton: 'Passwort aktualisieren',
  updatingPassword: 'Wird aktualisiert…',
  passwordUpdatedTitle: 'Passwort aktualisiert',
  passwordUpdatedMessage: 'Du kannst dich jetzt mit deinem neuen Passwort anmelden.',

  // PasswordChangeConfirmPage
  activatingPassword: 'Neues Passwort wird aktiviert…',
  passwordActivatedTitle: 'Passwort aktiviert',

  // EmailChangeConfirmPage
  activatingEmail: 'Neue E-Mail-Adresse wird aktiviert…',
  emailUpdatedTitle: 'E-Mail-Adresse aktualisiert',
  emailUpdatedMessage: (email: string) => `Deine Konto-E-Mail ist jetzt ${email}. Verwende sie beim nächsten Anmelden.`,

  // VerifyEmailPage
  verifyingEmail: 'E-Mail wird verifiziert…',
  emailVerifiedTitle: 'E-Mail bestätigt',
  emailVerifiedMessage: (email: string) => `Danke für die Bestätigung von ${email}. Dein Konto ist aktiv — du kannst dich jetzt anmelden.`,
  verificationFailed: 'Verifizierung fehlgeschlagen',

  // GoogleOAuthCallbackPage
  connectingGoogleCalendar: 'Google Kalender wird verbunden…',
  googleCalendarConnected: 'Google Kalender verbunden',
  googleCalendarSyncMessage: (email: string) => `Deine Termine werden jetzt mit ${email} synchronisiert.`,
  googleCalendarSyncMessageNoEmail: 'Deine Termine werden jetzt mit deinem Google Kalender synchronisiert.',
  googlePermissionsIncomplete: 'Berechtigungen unvollständig',
  googlePermissionsIncompleteMessage: 'Googles Zustimmungsbildschirm erlaubt es, jede Berechtigung einzeln zu aktivieren — eine oder mehrere wurden nicht erteilt. Die Synchronisierung funktioniert erst, wenn alle Berechtigungen erteilt wurden.',
  googlePermissionsRetryMessage: 'Bitte gehe zurück zum Dashboard, trenne Google Kalender in den Einstellungen und verbinde ihn erneut — diesmal alle Checkboxen aktivieren.',
  googleCalendarError: 'Google Kalender konnte nicht verbunden werden',
  backToDashboard: 'Zurück zum Dashboard',

  // BookingsOverview
  appointmentsHeading: 'Termine',
  exportIcsButton: '.ics exportieren',
  calendarFeedHeading: 'Kalender-Abonnement',
  calendarFeedDescription: 'Abonniere diese URL in Apple Kalender, Google Kalender oder Outlook, um deine Termine live zu synchronisieren.',
  calendarFeedCopied: 'Kopiert!',
  calendarFeedCopy: 'URL kopieren',
  calendarFeedRegenerate: 'Neu generieren',
  calendarFeedRegenerateWarning: 'Die alte URL wird ungültig. Bestehende Abonnements müssen aktualisiert werden.',
  includeDeclined: 'Abgelehnte Termine anzeigen',
  filterByMonth: 'Nach Monat filtern:',
  allMonths: 'Alle Monate',
  noAppointmentsYet: 'Noch keine Termine vorhanden.',
  noAppointmentsMonth: 'Keine Termine in diesem Monat gefunden.',
  colDate: 'Datum',
  colCustomer: 'Kunde',
  colLocation: 'Ort',
  colEmail: 'E-Mail',
  colTime: 'Uhrzeit',
  colDescription: 'Beschreibung',
  noLocation: '— Kein Ort —',
  saveButton: 'Speichern',
  savingButton: 'Wird gespeichert…',
  discardButton: 'Verwerfen',
  acceptButton: 'Annehmen',
  declineButton: 'Ablehnen',
  editButton: 'Bearbeiten',
  deleteButton: 'Löschen',
  cancelButton: 'Abbrechen',
  cancelAppointmentConfirm: 'Termin absagen? Der Kunde wird per E-Mail benachrichtigt.',
  paginationPrev: '← Zurück',
  paginationNext: 'Weiter →',
  paginationStatus: (page: number, totalPages: number, total: number) =>
    `Seite ${page} von ${totalPages} · ${total} gesamt`,

  // AvailabilityManager
  timeManagementHeading: 'Zeitverwaltung',
  tabWorkingHours: 'Arbeitszeiten',
  tabVacations: 'Freitage & Urlaub',
  manageWeeklyAvailability: 'Wöchentliche Verfügbarkeit verwalten',
  toLabel: 'bis',
  vacationManagementHeading: 'Urlaubsverwaltung',
  addNewVacation: 'Neuen Urlaub hinzufügen',
  failedLoadVacations: 'Urlaube konnten nicht geladen werden',
  failedLoadWorkingHours: 'Arbeitszeiten konnten nicht geladen werden.',
  failedSaveWorkingHours: 'Arbeitszeiten konnten nicht gespeichert werden. Bitte erneut versuchen.',
  failedResetWorkingHours: 'Arbeitszeiten konnten nicht zurückgesetzt werden. Bitte erneut versuchen.',
  workingHoursSaved: 'Arbeitszeiten gespeichert.',
  workingHoursReset: (day: string) => `Arbeitszeiten für ${day} zurückgesetzt.`,
  vacationFormTitleEdit: 'Urlaub bearbeiten',
  vacationFormTitleAdd: 'Neuen Urlaub hinzufügen',
  vacationTitleLabel: 'Titel:',
  vacationStartDateLabel: 'Startdatum:',
  vacationEndDateLabel: 'Enddatum:',
  updateVacationButton: 'Urlaub aktualisieren',
  createVacationButton: 'Urlaub erstellen',
  loadingVacations: 'Urlaube werden geladen...',
  noVacationsScheduled: 'Kein Urlaub geplant.',
  dismissToast: 'Schließen',
  validateBothHours: 'Bitte Start- und Endzeit eingeben.',
  validateStartHour: 'Startzeit muss zwischen 0 und 23 liegen.',
  validateEndHour: 'Endzeit muss zwischen 0 und 23 liegen.',
  validateEndAfterStart: 'Endzeit muss nach der Startzeit liegen.',
  failedSaveVacation: 'Urlaub konnte nicht gespeichert werden',
  failedDeleteVacation: 'Urlaub konnte nicht gelöscht werden',
  deleteVacationConfirm: 'Diesen Urlaub wirklich löschen?',

  // VacationManager
  vacationStatusActive: 'Aktiv',
  vacationStatusUpcoming: 'Bevorstehend',
  vacationStatusPast: 'Vergangen',
  addVacationButton: 'Urlaub hinzufügen',
  vacationTitlePlaceholder: 'z.B. Sommerurlaub (optional)',

  // Recurring closures
  recurringClosuresHeading: 'Regelmäßige freie Tage',
  recurringClosuresSubheading: 'Einen Wochentag alle N Wochen sperren – z.B. jeden 3. Donnerstag.',
  recurringClosureWeekdayLabel: 'Wochentag',
  recurringClosureIntervalLabel: 'Alle',
  recurringClosureIntervalUnit: 'Wochen',
  recurringClosureReferenceDateLabel: 'Erstes Datum',
  recurringClosureReferenceDateHint: 'Wähle ein beliebiges Datum, das auf den gewählten Wochentag fällt und ein Vorkommen dieses Musters ist.',
  recurringClosureAddButton: 'Hinzufügen',
  recurringClosureError: 'Freier Tag konnte nicht aktualisiert werden',
  recurringClosureWrongWeekday: 'Das Datum muss ein',
  recurringClosureDisplay: (interval: number, weekdayName: string) =>
    `Alle ${interval} Woche${interval > 1 ? 'n' : ''} am ${weekdayName}`,
  recurringClosureNext: 'Nächste:',

  // LocationManager
  locationsHeading: 'Orte',
  addLocationButton: 'Ort hinzufügen',
  locationDefaultHint: 'Der Standardort wird für Share-Links verwendet, sofern kein anderer gewählt wurde.',
  locationNameLabel: 'Name',
  locationAddressLabel: 'Adresse',
  locationUseAsDefault: 'Als Standard verwenden',
  updateButton: 'Aktualisieren',
  createButton: 'Erstellen',
  loadingLocations: 'Orte werden geladen…',
  noLocationsYet: 'Noch keine Orte vorhanden. Füge einen hinzu, damit Kunden wissen, wohin sie sollen.',
  defaultBadge: 'Standard',
  makeDefaultButton: 'Als Standard setzen',
  deleteLocationConfirm: (name: string) => `Ort „${name}" löschen?`,
  failedLoadLocations: 'Orte konnten nicht geladen werden',
  failedSaveLocation: 'Ort konnte nicht gespeichert werden',
  failedSetDefaultLocation: 'Standardort konnte nicht gesetzt werden',
  failedDeleteLocation: 'Ort konnte nicht gelöscht werden',

  // ProfileManager
  loadingProfile: 'Profil wird geladen...',
  errorLabel2: 'Fehler:',
  bookingHorizonTitle: 'Buchungshorizont',
  bookingHorizonHint: 'Wie viele Tage im Voraus Kunden buchen können. 7–365 Tage.',
  bookingHorizonSuccess: 'Buchungshorizont aktualisiert.',
  bookingHorizonError: 'Buchungshorizont konnte nicht aktualisiert werden',
  bookingHorizonValidate: 'Muss zwischen 7 und 365 Tagen liegen.',
  breakTimeTitle: 'Pause zwischen Terminen',
  breakTimeHint: 'Minuten, die nach jedem Termin reserviert werden, bevor der nächste beginnen kann. 0 = keine Pause.',
  breakTimeSuccess: 'Pause aktualisiert.',
  breakTimeError: 'Pause konnte nicht aktualisiert werden',
  breakTimeValidate: 'Muss zwischen 0 und 120 Minuten liegen.',

  // MfaSetupPage
  secureYourAccount: 'Konto absichern',
  mfaSetupIntro: 'Die Zwei-Faktor-Authentifizierung fügt eine starke zweite Schutzschicht über dein Passwort hinzu.',
  mfaSetupIntroStrong: 'Wir empfehlen dringend, sie zu aktivieren',
  mfaSetupIntroReason: '— ohne sie kann jeder, der dein Passwort kennt, auf dein Konto zugreifen.',
  mfaEmailLabel: 'E-Mail-Code',
  mfaEmailDesc: 'Erhalte bei jedem Login einen 6-stelligen Code per E-Mail.',
  mfaAppLabel: 'Authentifikator-App',
  mfaAppDesc: 'Nutze Google Authenticator, Authy oder eine beliebige TOTP-App.',
  mfaSkipForNow: 'Vorerst überspringen',
  mfaSkipHint: 'Du kannst die Zwei-Faktor-Authentifizierung später in den Einstellungen aktivieren.',
  mfaTotpInstructions: 'Öffne deine Authentifikator-App und füge ein neues Konto hinzu. Gib den Schlüssel unten manuell ein oder scanne ihn, falls deine App den URL-Import unterstützt:',
  mfaTotpHint: 'Gib dann den 6-stelligen Code aus deiner App zur Bestätigung ein:',
  mfaCodePlaceholder: '6-stelliger Code',
  mfaVerifying: 'Wird geprüft…',
  mfaConfirmEnable: 'Bestätigen & aktivieren',
  mfaBack: 'Zurück',
  mfaChallengeHeading: 'Zwei-Faktor-Authentifizierung',
  mfaChallengeTabTotp: 'Authentifikator-App',
  mfaChallengeTabEmail: 'E-Mail-Code',
  mfaChallengeHintEmail: 'Ein Bestätigungscode wurde an deine E-Mail-Adresse gesendet.',
  mfaChallengeHintTotp: 'Gib den Code aus deiner Authentifikator-App ein.',
  mfaCodePlaceholderChallenge: '6-stelliger Code',
  mfaVerify: 'Bestätigen',
  mfaResendCode: 'Code erneut senden',
  mfaBackToLogin: 'Zurück zum Login',
  mfaCouldNotSkip: 'Überspringen fehlgeschlagen – bitte erneut versuchen.',
  mfaFailedEnableEmail: 'E-Mail-MFA konnte nicht aktiviert werden.',
  mfaFailedStartSetup: 'Einrichtung konnte nicht gestartet werden.',
  mfaInvalidCode: 'Ungültiger Code – bitte erneut versuchen.',

  // Dashboard / ProfileOverlay
  editProfileHeading: 'Profil bearbeiten',
  closeProfileEditor: 'Profilbearbeitung schließen',

  // DashboardTopbar
  navAppointments: 'Termine',
  navAvailability: 'Verfügbarkeit',
  welcomeGreeting: (firstName: string) => `Willkommen, ${firstName}`,
  openProfileMenu: 'Profilmenü öffnen',
  menuSettings: 'Einstellungen',
  menuAbout: 'Über & Hilfe',
  menuSignOut: 'Abmelden',

  // AboutOverlay
  aboutHeading: 'Über & Hilfe',
  closeAbout: 'Schließen',
  aboutWorkingHoursTitle: 'Arbeitszeiten einrichten',
  aboutWorkingHoursBody: 'Gehe zu Einstellungen → Arbeitszeiten, um festzulegen, an welchen Tagen und Uhrzeiten du verfügbar bist. Kunden sehen nur Slots, die innerhalb deiner Arbeitszeiten liegen und nicht durch Urlaub oder wiederkehrende Schließungen blockiert sind.',
  aboutBookingLinkTitle: 'Dein Buchungslink',
  aboutBookingLinkBody: 'Teile deinen persönlichen Buchungslink mit Kunden, damit sie direkt einen Termin buchen können. Den Link findest du auf dem Dashboard. Optional kannst du in Einstellungen → Profil einen eigenen URL-Slug setzen.',
  aboutCalendarTitle: 'Kalender-Abonnement',
  aboutCalendarBody: 'In Einstellungen → Kalender findest du eine private Feed-URL, die du in Google Kalender, Apple Kalender oder eine andere Kalender-App einfügen kannst. Deine bestätigten Termine erscheinen dort automatisch und bleiben synchron.',
  aboutAppointmentsTitle: 'Termine verwalten',
  aboutAppointmentsBody: 'Neue Buchungsanfragen erscheinen in der Terminübersicht. Nimm sie mit einem Klick an oder lehne sie ab. Du kannst bestehende Termine auch stornieren oder eine Umbuchungsanfrage eines Kunden akzeptieren. Der Kunde erhält für jede Statusänderung eine E-Mail.',

  // SchedulingLinkCard
  schedulingLinkHeading: 'Buchungslink senden',
  chooseDurationLabel: 'Termindauer auswählen:',
  chooseAddressLabel: 'Terminort auswählen:',
  customerChooses: 'Kunde wählt',
  noLocationsHint: 'Noch keine Orte vorhanden – füge einen in den Einstellungen hinzu, damit Kunden wissen, wohin sie sollen.',
  copyButton: 'Kopieren',
  copiedButton: 'Kopiert!',
  publicIdHint: (id: string) => `Öffentliche ID: ${id}`,
  customPublicIdSuffix: ' (benutzerdefiniert)',

  // GoogleCalendarSection
  googleCalendarHeading: 'Google Kalender',
  googleCalendarDesc: 'Synchronisiere bestätigte und angenommene Termine mit deinem Google Kalender. Buchungen erscheinen automatisch als Ereignisse und werden bei Verschiebungen aktualisiert.',
  googleCalendarLoading: 'Wird geladen…',
  googleCalendarStatusConnected: 'Verbunden',
  googleDisconnecting: 'Wird getrennt…',
  googleDisconnect: 'Trennen',
  googleMissingScopes: 'Einige erforderliche Berechtigungen wurden nicht erteilt.',
  googleMissingScopesDetail: 'Googles Zustimmungsbildschirm erlaubt es, jede Berechtigung einzeln zu aktivieren – eine oder mehrere wurden nicht erteilt, sodass die Synchronisierung nicht vollständig funktioniert. Bitte trenne die Verbindung und verbinde erneut, diesmal alle Berechtigungen aktivieren.',
  googleSyncTo: 'Synchronisieren mit',
  googleLoadingCalendars: 'Kalender werden geladen…',
  googleNoWritableCalendars: 'Kein beschreibbarer Kalender auf diesem Google-Konto gefunden.',
  googleCalendarSwitching: 'Kalender wird gewechselt… bestehende Ereignisse werden im Hintergrund übertragen.',
  googleConnectButton: 'Google Kalender verbinden',
  googleRedirecting: 'Weiterleitung…',
  googleFailedLoadStatus: 'Google-Kalender-Status konnte nicht geladen werden',
  googleCouldNotLoadCalendars: 'Deine Google-Kalender konnten nicht geladen werden',
  googleCouldNotStartSignIn: 'Google-Anmeldung konnte nicht gestartet werden',
  googleCouldNotDisconnect: 'Google Kalender konnte nicht getrennt werden',
  googleCouldNotChangeCalendar: 'Kalender konnte nicht gewechselt werden',

  // DurationSettings
  durationSettingsHeading: 'Angebotene Termindauern',
  durationSettingsHint: 'Kunden wählen aus dieser Liste. Gesperrte Share-Links müssen einen dieser Werte verwenden.',
  durationRemoveAriaLabel: (d: number) => `${d}-Stunden-Option entfernen`,
  durationDefaultLabel: 'Standarddauer:',
  durationNoDefault: 'Kein Standard',
  durationAddPlaceholder: 'z. B. 1.5',
  durationAddButton: 'Hinzufügen',
  durationSavingButton: 'Wird gespeichert…',
  durationUpdated: 'Dauernoptionen aktualisiert',
  durationDefaultUpdated: 'Standarddauer aktualisiert',
  durationFailedUpdate: 'Dauernoptionen konnten nicht aktualisiert werden',
  durationFailedUpdateDefault: 'Standarddauer konnte nicht aktualisiert werden',
  durationErrorRange: 'Dauer muss zwischen 0,5 und 8,0 Stunden liegen',
  durationErrorDuplicate: 'Diese Dauer ist bereits in der Liste',
  durationErrorMinOne: 'Mindestens eine Dauernoption ist erforderlich',

  // ProfileForm
  firstNameLabel: 'Vorname',
  lastNameLabel: 'Nachname',
  emailAddressLabel: 'E-Mail-Adresse',
  updatingButton: 'Wird aktualisiert...',
  updateProfileButton: 'Profil aktualisieren',
  firstNameRequired: 'Vorname ist erforderlich',
  firstNameTooShort: 'Vorname muss mindestens 2 Zeichen lang sein',
  lastNameRequired: 'Nachname ist erforderlich',
  lastNameTooShort: 'Nachname muss mindestens 2 Zeichen lang sein',
  emailRequired: 'E-Mail-Adresse ist erforderlich',
  pendingEmailHint: (email: string) =>
    `Ausstehende Änderung zu ${email} — klicke auf den Link, den wir an diese Adresse gesendet haben, um sie zu aktivieren.`,
  profileUpdateFailed: 'Profil konnte nicht aktualisiert werden. Bitte erneut versuchen.',

  // MfaSettings
  twoFactorHeading: 'Zwei-Faktor-Authentifizierung',
  mfaEmailMethodLabel: 'E-Mail-Code',
  mfaEmailMethodDesc: 'Erhalte bei jedem Login einen 6-stelligen Code per E-Mail.',
  mfaAppMethodLabel: 'Authentifikator-App',
  mfaAppMethodDesc: 'Nutze Google Authenticator, Authy oder eine beliebige TOTP-App.',
  mfaDisableButton: 'Deaktivieren',
  mfaEnableButton: 'Aktivieren',
  mfaDisabling: 'Wird deaktiviert…',
  mfaConfirmButton: 'Bestätigen',
  mfaCurrentPasswordPlaceholder: 'Aktuelles Passwort',
  mfaTotpInstructionsSettings: 'Gib den Schlüssel unten in deine Authentifikator-App ein oder scanne ihn, falls sie den URL-Import unterstützt:',
  mfaTotpHintSettings: 'Gib dann den 6-stelligen Code zur Bestätigung ein:',
  mfaConfirmEnableButton: 'Bestätigen & aktivieren',
  mfaVerifyingButton: 'Wird geprüft…',
  mfaEmailEnabled: 'E-Mail-MFA aktiviert. Du erhältst bei jedem Login einen Code per E-Mail.',
  mfaEmailDisabled: 'E-Mail-MFA wurde deaktiviert.',
  mfaTotpEnabled: 'Authentifikator-App-MFA aktiviert.',
  mfaTotpDisabled: 'Authentifikator-App-MFA wurde deaktiviert.',
  mfaFailedEnableEmailSettings: 'E-Mail-MFA konnte nicht aktiviert werden.',
  mfaFailedDisableEmail: 'E-Mail-MFA konnte nicht deaktiviert werden.',
  mfaFailedStartTotp: 'TOTP-Einrichtung konnte nicht gestartet werden.',
  mfaInvalidCodeSettings: 'Ungültiger Code — bitte erneut versuchen.',
  mfaFailedDisableTotp: 'Authentifikator-MFA konnte nicht deaktiviert werden.',

  // CustomSlugForm
  customSlugHeading: 'Benutzerdefinierter Buchungslink',
  customSlugHint: '3–32 Zeichen, Kleinbuchstaben/Ziffern/Bindestriche. Leer lassen, um die zufällige ID beizubehalten.',
  customSlugPlaceholder: 'z. B. jane-tattoo',
  customSlugSaving: 'Wird gespeichert…',
  customSlugClear: 'Löschen',
  customSlugSave: 'Speichern',
  customSlugUpdated: 'Benutzerdefinierter Buchungslink aktualisiert.',
  customSlugCleared: 'Benutzerdefinierter Buchungslink gelöscht. Deine zufällige ID wird wieder verwendet.',
  customSlugFailed: 'Benutzerdefinierte ID konnte nicht aktualisiert werden',

  // NumericFieldForm
  numericSaving: 'Wird gespeichert…',

  // PasswordChangeForm
  changePasswordHeading: 'Passwort ändern',
  changePasswordHint: 'Wir senden dir einen Bestätigungslink an deine registrierte Adresse. Dein aktuelles Passwort bleibt gültig, bis du auf den Link klickst.',
  currentPasswordLabel: 'Aktuelles Passwort',
  newPasswordLabel: 'Neues Passwort',
  confirmNewPasswordLabel: 'Neues Passwort bestätigen',
  sendingButton: 'Wird gesendet…',
  changePasswordButton: 'Passwort ändern',
  newPasswordTooShort: 'Das neue Passwort muss mindestens 8 Zeichen lang sein.',
  passwordsDoNotMatch: 'Neues Passwort und Bestätigung stimmen nicht überein.',
  passwordChangeFailed: 'Passwortänderung konnte nicht angefordert werden',
  passwordChangeConfirmationSent: 'Bestätigungsmail gesendet. Klicke auf den Link, um dein neues Passwort zu aktivieren.',

  // LogoUploadForm
  profilePictureHeading: 'Profilbild',
  uploadingButton: 'Wird hochgeladen…',
  changePictureButton: 'Bild ändern',
  uploadPictureButton: 'Bild hochladen',
  removePictureButton: 'Entfernen',
  logoHint: 'JPEG, PNG, WebP oder GIF · max. 5 MB',
  logoAlt: 'Profilbild',
  logoUpdated: 'Bild aktualisiert.',
  logoUploadFailed: 'Upload fehlgeschlagen. Bitte erneut versuchen.',
  logoRemoveFailed: 'Bild konnte nicht entfernt werden.',

  // Bio on booking page
  bioSectionTitle: 'Über',

  // BioForm
  bioHeading: 'Über dich',
  bioLabel: 'Bio',
  bioPlaceholder: 'Eine kurze Vorstellung, die Kunden auf deiner Buchungsseite sehen (max. 500 Zeichen)',
  bioSaveButton: 'Bio speichern',
  bioSaving: 'Wird gespeichert…',
  bioSaved: 'Bio gespeichert.',
  bioSaveFailed: 'Bio konnte nicht gespeichert werden. Bitte erneut versuchen.',

  // Legal pages
  imprintTitle: 'Impressum',
  privacyTitle: 'Datenschutz',
  termsTitle: 'AGB',
  backToHome: 'Zur Startseite',
  back: 'Zurück',
};

export const translations: Record<Lang, typeof en> = { en, de };

export function getLang(): Lang {
  const stored = localStorage.getItem('lang');
  if (stored === 'de' || stored === 'en') return stored;
  return navigator.language.startsWith('de') ? 'de' : 'en';
}

export function setLangPersisted(lang: Lang): void {
  localStorage.setItem('lang', lang);
}
